import { useState, useEffect, useCallback, useRef } from "react";
import { Badge, IconButton } from "@compass/core";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ApiService from "services/apiService";
import { NotificationDto, NotificationListRequest } from "generated/openApi";
import { AppTopBarNotificationsModal } from "./AppTopBarNotificationsModal";
import {
    APP_TOP_BAR_NOTIFICATIONS_COPY,
    APP_TOP_BAR_NOTIFICATIONS_ERROR_MESSAGES,
    AppTopBarNotificationsTab,
    NEW_NOTIFICATIONS_PAGE_SIZE,
    NOTIFICATIONS_FIRST_PAGE,
    PREVIOUS_NOTIFICATIONS_PAGE_SIZE,
    UNREAD_COUNT_PAGE_SIZE,
} from "./AppTopBarNotificationsConstants";

const createUnreadNotificationListRequest = (
    page: number,
    pageSize: number
): NotificationListRequest => ({
    unreadOnly: true,
    page,
    pageSize,
});

const createReadNotificationListRequest = (
    page: number,
    pageSize: number
): NotificationListRequest => ({
    readOnly: true,
    page,
    pageSize,
});

export const AppTopBarNotifications = () => {
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [newNotifications, setNewNotifications] = useState<NotificationDto[]>([]);
    const [newNotificationsLoading, setNewNotificationsLoading] = useState<boolean>(false);
    const [newNotificationsError, setNewNotificationsError] = useState<boolean>(false);
    const [previousNotifications, setPreviousNotifications] = useState<NotificationDto[]>([]);
    const [previousNotificationsLoading, setPreviousNotificationsLoading] = useState<boolean>(false);
    const [previousNotificationsPaging, setPreviousNotificationsPaging] = useState<boolean>(false);
    const [previousNotificationsError, setPreviousNotificationsError] = useState<boolean>(false);
    const [previousNotificationsPageChangeError, setPreviousNotificationsPageChangeError] =
        useState<boolean>(false);
    const [previousPage, setPreviousPage] = useState<number>(NOTIFICATIONS_FIRST_PAGE);
    const [previousTotalCount, setPreviousTotalCount] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<AppTopBarNotificationsTab>(
        AppTopBarNotificationsTab.New
    );
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const activeTabRef = useRef<AppTopBarNotificationsTab>(AppTopBarNotificationsTab.New);
    const modalSessionIdRef = useRef<number>(0);
    const unreadCountRequestIdRef = useRef<number>(0);
    const newNotificationsRequestIdRef = useRef<number>(0);
    const previousRequestIdRef = useRef<number>(0);
    const newNotificationsLoadedRef = useRef<boolean>(false);
    const newNotificationsLoadedCountRef = useRef<number>(0);
    const previousNotificationsLoadedPageRef = useRef<number | null>(null);
    const markAllAsReadPromiseRef = useRef<Promise<unknown> | null>(null);
    const markAllAsReadSessionIdRef = useRef<number>(0);
    const fetchUnreadCount = useCallback(async () => {
        const requestId = ++unreadCountRequestIdRef.current;
        const markAllAsReadSessionId = markAllAsReadSessionIdRef.current;

        try {
            const response = await ApiService.notifications.getList(
                createUnreadNotificationListRequest(
                    NOTIFICATIONS_FIRST_PAGE,
                    UNREAD_COUNT_PAGE_SIZE
                )
            );

            if (
                requestId !== unreadCountRequestIdRef.current ||
                markAllAsReadSessionId !== markAllAsReadSessionIdRef.current
            ) {
                return;
            }

            setUnreadCount(response.totalCount);
        } catch (error) {
            if (
                requestId !== unreadCountRequestIdRef.current ||
                markAllAsReadSessionId !== markAllAsReadSessionIdRef.current
            ) {
                return;
            }

            console.error(APP_TOP_BAR_NOTIFICATIONS_ERROR_MESSAGES.unreadCount, error);
        }
    }, []);

    const fetchNewNotifications = useCallback(async () => {
        const requestId = ++newNotificationsRequestIdRef.current;
        const modalSessionId = modalSessionIdRef.current;

        setNewNotificationsLoading(true);
        try {
            const pendingMarkAllAsRead = markAllAsReadPromiseRef.current;
            if (pendingMarkAllAsRead) {
                try {
                    await pendingMarkAllAsRead;
                } catch {
                    // The close handler reports the failure. Fetch the current server state either way.
                }
            }

            if (
                requestId !== newNotificationsRequestIdRef.current ||
                modalSessionId !== modalSessionIdRef.current ||
                activeTabRef.current !== AppTopBarNotificationsTab.New
            ) {
                return;
            }

            const response = await ApiService.notifications.getList(
                createUnreadNotificationListRequest(
                    NOTIFICATIONS_FIRST_PAGE,
                    NEW_NOTIFICATIONS_PAGE_SIZE
                )
            );

            if (
                requestId !== newNotificationsRequestIdRef.current ||
                modalSessionId !== modalSessionIdRef.current ||
                activeTabRef.current !== AppTopBarNotificationsTab.New
            ) {
                return;
            }

            const loadedNotifications = response.notifications ?? [];
            unreadCountRequestIdRef.current += 1;
            setNewNotifications(loadedNotifications);
            setUnreadCount(response.totalCount);
            setNewNotificationsError(false);
            newNotificationsLoadedRef.current = true;
            newNotificationsLoadedCountRef.current = loadedNotifications.length;
        } catch (error) {
            if (
                requestId !== newNotificationsRequestIdRef.current ||
                modalSessionId !== modalSessionIdRef.current ||
                activeTabRef.current !== AppTopBarNotificationsTab.New
            ) {
                return;
            }

            console.error(APP_TOP_BAR_NOTIFICATIONS_ERROR_MESSAGES.newNotifications, error);
            setNewNotificationsError(true);
        } finally {
            if (
                requestId === newNotificationsRequestIdRef.current &&
                modalSessionId === modalSessionIdRef.current
            ) {
                setNewNotificationsLoading(false);
            }
        }
    }, []);

    const fetchPreviousNotifications = useCallback(async (page: number, isPageChange: boolean) => {
        const requestId = ++previousRequestIdRef.current;
        const modalSessionId = modalSessionIdRef.current;
        let requestedPage = page;

        previousNotificationsLoadedPageRef.current = null;
        setPreviousNotificationsPageChangeError(false);
        if (isPageChange) {
            setPreviousNotificationsError(false);
            setPreviousNotificationsPaging(true);
        } else {
            setPreviousNotificationsLoading(true);
        }
        try {
            const pendingMarkAllAsRead = markAllAsReadPromiseRef.current;
            if (pendingMarkAllAsRead) {
                try {
                    await pendingMarkAllAsRead;
                } catch {
                    // The close handler reports the failure. Fetch the current server state either way.
                }
            }

            if (
                requestId !== previousRequestIdRef.current ||
                modalSessionId !== modalSessionIdRef.current ||
                activeTabRef.current !== AppTopBarNotificationsTab.Previous
            ) {
                return;
            }

            while (true) {
                const response = await ApiService.notifications.getList(
                    createReadNotificationListRequest(
                        requestedPage,
                        PREVIOUS_NOTIFICATIONS_PAGE_SIZE
                    )
                );

                if (
                    requestId !== previousRequestIdRef.current ||
                    modalSessionId !== modalSessionIdRef.current ||
                    activeTabRef.current !== AppTopBarNotificationsTab.Previous
                ) {
                    return;
                }

                const totalPages = Math.ceil(
                    response.totalCount / PREVIOUS_NOTIFICATIONS_PAGE_SIZE
                );
                if (totalPages > 0 && requestedPage > totalPages) {
                    requestedPage = totalPages;
                    setPreviousPage(requestedPage);
                    continue;
                }

                const loadedPage = totalPages === 0
                    ? NOTIFICATIONS_FIRST_PAGE
                    : requestedPage;
                setPreviousNotifications(response.notifications ?? []);
                setPreviousTotalCount(response.totalCount);
                setPreviousPage(loadedPage);
                setPreviousNotificationsError(false);
                setPreviousNotificationsPageChangeError(false);
                previousNotificationsLoadedPageRef.current = loadedPage;
                break;
            }
        } catch (error) {
            if (
                requestId !== previousRequestIdRef.current ||
                modalSessionId !== modalSessionIdRef.current ||
                activeTabRef.current !== AppTopBarNotificationsTab.Previous
            ) {
                return;
            }

            console.error(APP_TOP_BAR_NOTIFICATIONS_ERROR_MESSAGES.previousNotifications, error);
            previousNotificationsLoadedPageRef.current = null;
            setPreviousNotificationsError(true);
            setPreviousNotificationsPageChangeError(isPageChange);
        } finally {
            if (
                requestId === previousRequestIdRef.current &&
                modalSessionId === modalSessionIdRef.current
            ) {
                setPreviousNotificationsLoading(false);
                setPreviousNotificationsPaging(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchUnreadCount();

        return () => {
            unreadCountRequestIdRef.current += 1;
            newNotificationsRequestIdRef.current += 1;
            previousRequestIdRef.current += 1;
            modalSessionIdRef.current += 1;
            markAllAsReadSessionIdRef.current += 1;
            previousNotificationsLoadedPageRef.current = null;
        };
    }, [fetchUnreadCount]);

    const handleBellClick = () => {
        modalSessionIdRef.current += 1;
        activeTabRef.current = AppTopBarNotificationsTab.New;
        setActiveTab(AppTopBarNotificationsTab.New);
        setModalOpen(true);
        fetchNewNotifications();
    };

    const handleTabChange = (tab: AppTopBarNotificationsTab) => {
        if (tab === activeTabRef.current) return;

        if (activeTabRef.current === AppTopBarNotificationsTab.New) {
            newNotificationsRequestIdRef.current += 1;
            setNewNotificationsLoading(false);
        } else {
            previousRequestIdRef.current += 1;
            previousNotificationsLoadedPageRef.current = null;
            setPreviousNotificationsLoading(false);
            setPreviousNotificationsPaging(false);
        }

        activeTabRef.current = tab;
        setActiveTab(tab);
        setNewNotificationsError(false);
        setPreviousNotificationsError(false);
        setPreviousNotificationsPageChangeError(false);

        if (
            tab === AppTopBarNotificationsTab.Previous &&
            previousNotificationsLoadedPageRef.current !== previousPage
        ) {
            fetchPreviousNotifications(previousPage, false);
        } else if (
            tab === AppTopBarNotificationsTab.New &&
            !newNotificationsLoadedRef.current
        ) {
            fetchNewNotifications();
        }
    };

    const handlePreviousPageChange = (page: number) => {
        if (
            page < NOTIFICATIONS_FIRST_PAGE ||
            previousNotificationsLoading ||
            previousNotificationsPaging
        ) return;

        setPreviousPage(page);
        fetchPreviousNotifications(page, true);
    };

    const handleRetry = () => {
        if (activeTab === AppTopBarNotificationsTab.New) {
            fetchNewNotifications();
            return;
        }

        fetchPreviousNotifications(previousPage, previousNotificationsPageChangeError);
    };

    const handleModalClose = async () => {
        modalSessionIdRef.current += 1;
        const closeSessionId = modalSessionIdRef.current;
        newNotificationsRequestIdRef.current += 1;
        previousRequestIdRef.current += 1;
        activeTabRef.current = AppTopBarNotificationsTab.New;
        const markAllAsReadRequest = newNotificationsLoadedRef.current &&
            newNotificationsLoadedCountRef.current > 0
            ? ApiService.notifications.markAllAsRead()
            : undefined;
        if (markAllAsReadRequest) {
            markAllAsReadSessionIdRef.current += 1;
            markAllAsReadPromiseRef.current = markAllAsReadRequest;
        }

        setModalOpen(false);
        setActiveTab(AppTopBarNotificationsTab.New);
        setNewNotifications([]);
        setNewNotificationsLoading(false);
        setNewNotificationsError(false);
        setPreviousNotifications([]);
        setPreviousNotificationsLoading(false);
        setPreviousNotificationsPaging(false);
        setPreviousNotificationsError(false);
        setPreviousNotificationsPageChangeError(false);
        setPreviousPage(NOTIFICATIONS_FIRST_PAGE);
        setPreviousTotalCount(0);
        newNotificationsLoadedRef.current = false;
        newNotificationsLoadedCountRef.current = 0;
        previousNotificationsLoadedPageRef.current = null;

        if (markAllAsReadRequest) {
            try {
                await markAllAsReadRequest;
                if (closeSessionId === modalSessionIdRef.current) {
                    setUnreadCount(0);
                }
            } catch (error) {
                console.error(APP_TOP_BAR_NOTIFICATIONS_ERROR_MESSAGES.markAllAsRead, error);
            } finally {
                if (markAllAsReadPromiseRef.current === markAllAsReadRequest) {
                    markAllAsReadPromiseRef.current = null;
                }
            }
        }
    };

    return (
        <>
            <IconButton
                onClick={handleBellClick}
                color="inherit"
                aria-label={`${APP_TOP_BAR_NOTIFICATIONS_COPY.bellButtonLabel}: ${unreadCount} ${APP_TOP_BAR_NOTIFICATIONS_COPY.unreadLabel}`}
            >
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    invisible={unreadCount === 0}
                >
                    <NotificationsNoneOutlinedIcon color="action" />
                </Badge>
            </IconButton>

            <AppTopBarNotificationsModal
                open={modalOpen}
                activeTab={activeTab}
                newNotifications={newNotifications}
                newNotificationsLoading={newNotificationsLoading}
                newNotificationsError={newNotificationsError}
                previousNotifications={previousNotifications}
                previousNotificationsLoading={previousNotificationsLoading}
                previousNotificationsPaging={previousNotificationsPaging}
                previousNotificationsError={previousNotificationsError}
                previousPage={previousPage}
                previousTotalCount={previousTotalCount}
                unreadCount={unreadCount}
                onTabChange={handleTabChange}
                onPreviousPageChange={handlePreviousPageChange}
                onRetry={handleRetry}
                onClose={handleModalClose}
            />
        </>
    );
};
